import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox} from "@material-tailwind/react";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {Input} from "@material-tailwind/react/components/Input";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {getError, hasError} from "../../../hooks/errors";
import {clear, validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {Provider} from "../../../types/Responses/ProvidersResponse";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {useUserContext} from "../../../types/contexts/UserContext";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {setSidebarTitle} from "../../../reducers/sidebar";

const ProviderForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const toast = useToastContext();
    const navigate = useNavigate();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const [name, setName] = useState<string>();
    const [params, setParams] = useState<object>({callback_time: 604800});
    const [direct, setDirect] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [showNewFieldForm, setShowNewFieldForm] = useState<boolean>(false);
    const [fieldName, setFieldName] = useState<string>();
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const dispatch = useDispatch();
    const valid = useSelector((state: RootState) => state.validation.valid);
    const errors = useSelector((state: RootState) => state.validation.errors);

    const changeName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setName(e.currentTarget.value)
        }
    }, []);

    const changeFieldName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFieldName(e.currentTarget.value)
        }
    }, []);

    const changeParameterValue = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined, field: string) => {
        if (e?.currentTarget) {
            const value = JSON.parse(e.currentTarget.value) !== null ? JSON.parse(e.currentTarget.value) : e.currentTarget.value;
            setValidated(false);
            setParams({...params, [field as keyof typeof params]: value});
        }
    }, [params]);

    const handleSuccessResponse = useCallback((response: AxiosResponse, message: JSX.Element | string) => {
        const provider = response.data.data as Provider
        if (provider) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            navigate('/app/admin/providers');
        }
    }, [toast, navigate]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        setInProgress(false);
        if (b) {
            const request = {name, direct_redirect: direct, params};
            API.updateProvider(parseInt(id ?? ''), request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.providers.updated.title', {name}))).catch(e => ErrorHandler(e, `put${APILink.GET_PROVIDER.replace('$provider', id?.toString() ?? '')}`, toast, handleError));
        }
    }, [name, direct, params, toast, handleError, handleSuccessResponse]);

    const submit = useCallback(() => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [{key: 'name', label: t('admin.providers.table.name'), type: InputType.text, regex: '^.{4,}\\b$'}],
                introducedData: {name: name},
                callback: callback
            }
        }));
    }, [name, direct, callback]);

    const onAddNewField = useCallback(() => {
        setParams({...params, [fieldName?.toLowerCase()?.replace(' ', '_') as keyof typeof params]: ""});
        setFieldName('');
        setShowNewFieldForm(false);
    }, [params, fieldName]);

    const getSingleProvider = useCallback(() => {
        API.getProvider(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const providerResponse = response.data.data as Provider
            setName(providerResponse.name);
            setDirect(providerResponse.direct_redirect);
            setParams(providerResponse.params)
            setFormIsReady(true);
            setTitle(t('admin.providers.update.title', {name: providerResponse.name}));
            dispatch(setSidebarTitle(t('admin.providers.update.title', {name: providerResponse.name})));
        }).catch(e => {
            setFormIsReady(true)
            ErrorHandler(e, `get${APILink.GET_PROVIDER.replace('$provider', id?.toString() ?? '')}`, toast, handleError, setIsForbidden)
        });
    }, [setName, setDirect, setParams, setFormIsReady, toast, handleError, dispatch, id]);

    useEffect(() => {
        setTitle(t('admin.providers.update.title', {name: ''}));
        dispatch(setSidebarTitle(t('admin.providers.update.title', {name: ''})));
        if (user && user.user){
            checkEnabled2FA(user.user).then(() => {
                if (id) {
                    getSingleProvider();
                } else {
                    toast?.show(t('admin.providers.create_error'));
                    return;
                }
            }).catch(e => console.log(e));
        }
        dispatch(clear())
    }, [dispatch, id, getSingleProvider, handleError, toast, user]);

    const paramsUI = useMemo(() => {
        if (!params)
            return <></>

        const enteties = Object.entries(params);
        return <>{enteties.map((item, index) => {
            const itemName = item[0].replace('_', ' ')

            return <div className="max-w-md mt-4" key={`item_${index}`}>
                <Input success={valid && validated} error={validated && !valid} crossOrigin=''
                       variant={'outlined'} label={itemName.substring(0, 1).toUpperCase() + itemName.substring(1)}
                       type={'text'} onChange={(e) => changeParameterValue(e, item[0])}
                       value={["array", "object"].includes(typeof item[1]) ? JSON.stringify(item[1]) : item[1]}
                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                {hasError(item[0], errors) &&
                    <label key={'error-' + item[0]}
                           className="mt-1 text-red-500 text-xs block">{getError(item[0], errors)}</label>}
            </div>
        })}</>
    }, [params, valid, validated]);

    const newParamsForm = useMemo(() => {
        if (!showNewFieldForm)
            return <div className="max-w-md mt-8 mb-8">
                <Button className="bg-black text-white" onClick={() => setShowNewFieldForm(true)}>{t('admin.providers.add_new_field')}</Button>
            </div>;

        return <div className="max-w-md mt-8 mb-8">
            <Input crossOrigin=''
                   variant={'outlined'} label={t('admin.providers.field_name')} type={'text'} onChange={changeFieldName}
                   value={fieldName}
                   className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                   labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>

            <Button className="bg-black text-white mt-2" onClick={onAddNewField}>{t('Add')}</Button>
        </div>
    }, [showNewFieldForm, fieldName, onAddNewField]);

    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {!isForbidden && <>
                            <div className="max-w-md">
                                <Input success={valid && validated} error={validated && !valid} crossOrigin=''
                                       variant={'outlined'} label={t('admin.providers.table.name')} type={'text'} onChange={changeName}
                                       value={name}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {hasError('name', errors) &&
                                    <label key={'error-name'}
                                           className="mt-1 text-red-500 text-xs block">{getError('name', errors)}</label>}
                            </div>
                            {paramsUI}
                            {newParamsForm}
                            <div className="max-w-md mt-4">
                                <Checkbox checked={direct}
                                          onChange={() => setDirect(!direct)}
                                          color={'teal'} crossOrigin={''}
                                          value={1} label={<span
                                    className="dark:text-gray-500">{t('admin.providers.table.direct_redirect')}</span>}/>
                            </div>
                            <div className="mt-12">
                                <Button onClick={submit} className="text-white bg-teal-500"
                                        disabled={isInProgress}>{id ? t('admin.buttons.update') : t('admin.buttons.create')}</Button>
                            </div>
                        </>}
                        <Forbidden isForbidden={isForbidden}/>
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    );
}
export default memo(ProviderForm);
